// useLinkInBio — the Assets → Link-in-Bio data hooks: the editor read (manual rows + DERIVED auto
// rows + tour-dates link + short bio), the whole-set PUT, and the Stats read. The PUT body is ONE ordered
// `entries` list holding both kinds: a manual row carries its label/url, an auto row carries only its
// `source` and `hidden` (its text belongs to the connection). Position is the array index across both.
// Every read forces the tenant key
// ['band', bandId, …] via useBandQuery; the write invalidates it on success. The editor GET·PUT is assets:write; the
// Stats GET is assets:read (the screen cues each with useCapability). The short bio the editor edits is
// core.bands.short_bio via the shared bios hooks (useBrandAssets) — this hook only carries the link-in-bio DTOs.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type LinkInBioRead = components["schemas"]["LinkInBioRead"];
export type LinkRow = components["schemas"]["LinkRow"];
export type LinkEntryInput = components["schemas"]["LinkEntryInput"];
export type AutoLinkRow = components["schemas"]["AutoLinkRow"];
export type TourDatesLink = components["schemas"]["TourDatesLink"];
export type UpdateLinkInBioRequest = components["schemas"]["UpdateLinkInBioRequest"];
export type LinkClickBar = components["schemas"]["LinkClickBar"];

// The editor read carries LOCAL DRAFT state in the screen (keyed to remount on a deliberate save invalidation), so a
// BACKGROUND refetch must never clobber unsaved rows: staleTime:Infinity + refetchOnWindowFocus/Reconnect:false
// override the app-client defaults (the draft-clobber fix). dataUpdatedAt only moves on the initial load, an
// explicit Retry, or the save invalidateQueries (which refetches regardless of staleTime).
const EDITOR_READ = { staleTime: Infinity, refetchOnWindowFocus: false, refetchOnReconnect: false } as const;

export function useLinkInBio() {
  const { api } = useApp();
  return useBandQuery<LinkInBioRead>(
    ["link-in-bio"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/link-in-bio", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    EDITOR_READ,
  );
}

export function useUpdateLinkInBio() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<LinkInBioRead, UpdateLinkInBioRequest>(
    async (band, body) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/link-in-bio", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    { onSuccess: () => void qc.invalidateQueries({ queryKey: ["band", bandId, "link-in-bio"] }) },
  );
}


// ---- Merch shelf (ART-234) ------------------------------------------------------------------------------------
// The Link-in-Bio MERCH section: the band's store catalogue (served from the merch sync's cache — no vendor call),
// its saved picks, and the server-resolved `showing` list the public page renders. Saved on each change rather than
// with the links' Save button: it is its own PUT and its own small surface, and may move to a merch page later.

export type MerchShelfRead = components["schemas"]["MerchShelfRead"];
export type MerchShelfProduct = components["schemas"]["MerchShelfProduct"];
export type UpdateMerchShelfRequest = components["schemas"]["UpdateMerchShelfRequest"];

/** How many products the shelf shows — mirrors `BandMerchShelf.MaxPicks` (the server refuses more). */
export const MERCH_SHELF_MAX = 6;

export function useMerchShelf() {
  const { api } = useApp();
  return useBandQuery<MerchShelfRead>(["link-in-bio", "merch"], async (bandId) => {
    const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/link-in-bio/merch", {
      params: { path: { bandId } },
    });
    if (!response.ok || !data?.data) throw new ApiError(response.status, error);
    return data.data;
  });
}

export function useUpdateMerchShelf() {
  const { api } = useApp();
  const qc = useQueryClient();
  const { bandId } = useBandContext();
  return useBandMutation<MerchShelfRead, UpdateMerchShelfRequest>(
    async (band, body) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/link-in-bio/merch", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    // The PUT answers the whole recomposed shelf, so it IS the new cache entry — no refetch round trip.
    { onSuccess: (saved) => qc.setQueryData(["band", bandId, "link-in-bio", "merch"], saved) },
  );
}
